import React, { createContext, useContext, useRef, useEffect } from 'react';
import EventBus from './EventBus';
import logger from 'shared/utils/logging';

/**
 * React Context for EventBus
 */
const EventBusContext = createContext(null);

/**
 * EventBusProvider - Provides EventBus instance to React component tree
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} [props.options] - EventBus configuration options
 * @param {boolean} [props.options.enableDebug=false] - Enable debug logging
 * @param {Function} [props.options.logger=console] - Custom logger
 * @returns {React.ReactElement}
 *
 * @example
 * <EventBusProvider options={{ enableDebug: true }}>
 *   <App />
 * </EventBusProvider>
 */
export const EventBusProvider = ({ children, options = {} }) => {
  // Create EventBus instance once and store in ref to prevent recreation
  const eventBusRef = useRef(null);

  if (eventBusRef.current === null) {
    eventBusRef.current = new EventBus({
      enableDebug: process.env.NODE_ENV === 'development' || options.enableDebug,
      logger: options.logger || console,
      ...options
    });

    // Add global error handler middleware in development
    if (process.env.NODE_ENV === 'development') {
      eventBusRef.current.use((eventName, payload) => {
        // Log all events in development for debugging
        if (options.enableDebug) {
          logger.debug(`[EventBus] ${eventName}`, payload);
        }
        return payload;
      });
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventBusRef.current) {
        eventBusRef.current.clear();
        if (options.enableDebug) {
          logger.debug('[EventBusProvider] Cleared all subscriptions on unmount');
        }
      }
    };
  }, [options.enableDebug]);

  return (
    <EventBusContext.Provider value={eventBusRef.current}>
      {children}
    </EventBusContext.Provider>
  );
};

/**
 * useEventBus - Hook to access EventBus instance
 *
 * @returns {EventBus} EventBus instance
 * @throws {Error} If used outside EventBusProvider
 *
 * @example
 * const eventBus = useEventBus();
 * eventBus.subscribe('user:login', handleLogin);
 */
export const useEventBus = () => {
  const eventBus = useContext(EventBusContext);

  if (eventBus === null) {
    throw new Error('useEventBus must be used within an EventBusProvider');
  }

  return eventBus;
};

/**
 * useEventSubscription - Hook to subscribe to events with automatic cleanup
 *
 * @param {string} eventName - Event name to subscribe to
 * @param {Function} handler - Event handler function
 * @param {Object} [options] - Subscription options
 * @param {number} [options.priority=0] - Handler priority
 * @param {Array} [deps=[]] - Dependencies array (like useEffect)
 *
 * @example
 * useEventSubscription('robot:123:status', (status) => {
 *   console.log('Robot status:', status);
 * }, { priority: 10 }, [robotId]);
 */
export const useEventSubscription = (eventName, handler, options = {}, deps = []) => {
  const eventBus = useEventBus();

  useEffect(() => {
    if (!eventName || !handler) {
      return;
    }

    // Create AbortController for this subscription
    const abortController = new AbortController();

    // Subscribe with AbortSignal
    const unsubscribe = eventBus.subscribe(eventName, handler, {
      ...options,
      signal: abortController.signal
    });

    // Cleanup function
    return () => {
      abortController.abort();
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, eventName, ...deps]);
};

/**
 * useEventPublisher - Hook to get a publish function for a specific event
 *
 * @param {string} eventName - Event name to publish
 * @returns {Function} Publish function that takes payload
 *
 * @example
 * const publishStatus = useEventPublisher('robot:123:status');
 * publishStatus({ battery: 80 });
 */
export const useEventPublisher = (eventName) => {
  const eventBus = useEventBus();

  return React.useCallback((payload) => {
    eventBus.publish(eventName, payload);
  }, [eventBus, eventName]);
};

/**
 * useEventStats - Hook to get EventBus statistics
 *
 * @returns {Object} EventBus statistics
 *
 * @example
 * const stats = useEventStats();
 * console.log(`Events published: ${stats.eventsPublished}`);
 */
export const useEventStats = () => {
  const eventBus = useEventBus();
  const [stats, setStats] = React.useState(eventBus.getStats());

  useEffect(() => {
    // Update stats every second
    const interval = setInterval(() => {
      setStats(eventBus.getStats());
    }, 1000);

    return () => clearInterval(interval);
  }, [eventBus]);

  return stats;
};

export default EventBusProvider;
